import React, {useEffect, useState} from 'react'
import {useRecoilValue} from 'recoil'
import {WSPScorecardAtom} from '../../../State/WSPScorecard.Atom'
import {EntriesPerPage} from '../../../Component/Model/Table/Table'
import {WorkforceProfileService} from '../../../Component/Api/WorkforceProfile/WorkforceProfileService'
import {WorkforceProfile} from '../../../Component/Model/Dto/WSP/WorkforceProfile'
import {ShowMissingDataMessage, WorkforceProfilePreviewMapper} from './WorkforceProfilePreviewTableColumns'
import {DataTable} from '../../../Component/Common/Components/DataTable/DataTable'
import {HubConnection, HubConnectionBuilder, JsonHubProtocol, LogLevel} from '@microsoft/signalr'
import {Alert, Box, Button, Grid, Typography} from '@mui/material'
import {MDProgress} from '../../../Component/Common/Components/MDProgress'
import {MDTypography} from '../../../Component/Common/Components/MDTypography'
import {toast} from 'react-toastify'
import {DataImportType} from '../../../Component/Model/Enum/BEE123Enums'
import {UserAtom} from '../../../State/user.Atom'
import {ExportKeyValue} from '../../../Component/Model/Dto/Upload'
import {Export} from '../../../Component/Model/Dto/Export'
import {CoreExportService} from '../../../Component/Api/Export/CoreExportService'
import config from '../../../config'
import {MDAlert} from '../../../Component/Common/Components/MDAlert'
import {useSignalRConnection} from '../../../Hooks/useSignalRConnection'

interface IProps {
    isErrorReport: boolean;
}

export const WorkforceProfilePreview = (props: IProps) => {
    const WSPScorecard = useRecoilValue(WSPScorecardAtom);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string>("");
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({defaultValue: 10});
    const [workforceProfileData, setWorkforceProfileData] = useState<WorkforceProfile[]>([])
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [filteredItemCount, setFilteredItemCount] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [connection, setConnection] = useState<null | HubConnection>(null);
    const [progress, setProgress] = useState(0);
    const [fileExporting, setFileExporting] = useState<boolean>(false);
    const user = useRecoilValue(UserAtom);
    const hub = useSignalRConnection("ImportHub");

  const entriesPerPageChanged = (entriesPerPage: number) => {
    setEntriesPerPage({ defaultValue: entriesPerPage });
    setPageNumber(1);
  };

  const pageNumberChanged = (pageNumber: number) => {
    setPageNumber(pageNumber);
  };

  const searchValueChanged = (value: string) => {
    setSearchValue(value);
    setPageNumber(1);
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      let workforceProfilePreviewResult = await WorkforceProfileService.getWorkforceProfilePreview(WSPScorecard.wspScorecardId, 1, pageNumber, entriesPerPage.defaultValue!, props.isErrorReport, searchValue);
      setWorkforceProfileData(workforceProfilePreviewResult.data.results);
      setFilteredItemCount(workforceProfilePreviewResult.data.filteredItemsCount);
      setTotalItemsCount(workforceProfilePreviewResult.data.totalItemsCount);
    } catch (error) {
      console.error(error);
      setTotalItemsCount(1);
    } finally {
      setIsLoading(false);
    }

  }

    useEffect(() => {
        (async () => {
            try {
                if (!hub.isConnected) {
                    await hub.start();
                }

                hub.on(`exportProgress-${user.username}-${DataImportType[DataImportType.WorkforceProfileReport]}`, (data: any) => {
                    setFileExporting(true);
                })
                hub.on(`exportProgressUpdate-${user.username}-${DataImportType[DataImportType.WorkforceProfileReport]}`, (data: any) => {
                    setProgress(data.progress);
                    setFileExporting(true);
                })
                hub.on(`exportProgressFailed-${user.username}-${DataImportType[DataImportType.WorkforceProfileReport]}`, (data: any) => {
                    setFileExporting(false);
                    toast.error("Export Failed");
                })
                hub.on(`exportProgressCompleted-${user.username}-${DataImportType[DataImportType.WorkforceProfileReport]}`, (data: any) => {
                    setFileExporting(false);
                    setProgress(0);
                    window.open(data.ExportUrl, "_blank");
                })
            } catch (error) {
                console.error("SignalR connection failed:", error);
            }
        })();
    }, []);

  const onExportData = async () => {
    setIsLoading(true)
    let params: ExportKeyValue[] = [];
    params.push({ key: "searchValue", value: searchValue });
    params.push({ key: "setaTypeId", value: "1" });
    params.push({ key: "setaScorecardId", value: WSPScorecard.wspScorecardId })
    const data: Export.DynamicFileExportDto = {
      importType: DataImportType.WorkforceProfileReport,
      parameters: params
    }
    setFileExporting(true);
    try {
      await CoreExportService.FileExportFunction(data);
    } catch (error) {
      console.log(error);
      toast.error("Export Failed");
      setIsLoading(false);
    } finally {
      params = [];
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [WSPScorecard.wspScorecardId, entriesPerPage, pageNumber, searchValue]);

  const workforceProfileDataTable = WorkforceProfilePreviewMapper(workforceProfileData, props.isErrorReport);
  const showWarningMessage = ShowMissingDataMessage(workforceProfileData);
  return (
    <>
      {showWarningMessage && props.isErrorReport && (<>
        <Box pb={0} sx={{ width: '100%' }}>
          <Alert severity="warning" style={{ paddingTop: 5 }}>
            The highlighted cells represent missing information and this may cause errors when uploading the exported file onto the SETA website.
            Please ensure that all people and training data is fully captured.
          </Alert>
        </Box>
        <DataTable
          table={workforceProfileDataTable}
          entriesPerPage={entriesPerPage}
          pageNumber={pageNumber}
          filteredItemsCount={filteredItemCount}
          totalItemsCount={totalItemsCount}
          entriesPerPageChanged={entriesPerPageChanged}
          pageNumberChanged={pageNumberChanged}
          searchValueChanged={searchValueChanged}
          isLoading={isLoading}
          canSearch={true}

        />
      </>)}
      {props.isErrorReport && !showWarningMessage && !isLoading &&
        <Box pb={0} sx={{ width: '100%' }}>
          <Alert color='success' severity="success" style={{ paddingTop: 5 }}>
          We have not detected any missing information in your Workforce Profile Report.
          </Alert>
        </Box>
      }
      {!props.isErrorReport &&
      <>
        <DataTable
          table={workforceProfileDataTable}
          entriesPerPage={entriesPerPage}
          pageNumber={pageNumber}
          filteredItemsCount={filteredItemCount}
          totalItemsCount={totalItemsCount}
          entriesPerPageChanged={entriesPerPageChanged}
          pageNumberChanged={pageNumberChanged}
          searchValueChanged={searchValueChanged}
          isLoading={isLoading}
          canSearch={true}
          showSearchBoxInfoIcon = {true}
          searchBoxInfoIconTooltipText='Search by Employee ID, First Name and Last Name.'
        />
        <MDAlert color='light' mt={3}>
        {fileExporting ?
          <Grid container justifyContent="center" alignItems="center" direction="column">
            <Typography variant="h6" mb={1}>Exporting file...</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <MDProgress variant="determinate" value={progress} style={{ width: '100%' }} />
              </Box>
              <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">{`${Math.round(progress)}%`}</Typography>
              </Box>
            </Box>
          </Grid> :
          <MDTypography component='span' variant='body' fontSize='0.93rem' fontWeight='regular'>
            <p style={{ marginBottom: "0.2rem" }}> Click<Button variant="text" onClick={onExportData} size="small" style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>here</Button>
              to Export data to a file</p>
          </MDTypography>
        }
      </MDAlert>
      </>}
     
    </>
  )
}
